import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const InviteHandler = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [therapistInfo, setTherapistInfo] = useState(null);
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [showRegistrationOption, setShowRegistrationOption] = useState(false);

  useEffect(() => {
    const handleInvite = async () => {
      const token = localStorage.getItem('therapy_auth_token');
      
      if (!token) {
        // User not logged in - first validate the invitation code
        try {
          const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          const validateResponse = await fetch(`${API_BASE}/api/invitations/validate-invitation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              invitation_code: code.toUpperCase()
            })
          });

          if (validateResponse.ok) {
            const invitationData = await validateResponse.json();
            setInvitationInfo(invitationData);
            setShowRegistrationOption(true);
            setLoading(false);
            return;
          } else {
            const errorData = await validateResponse.json();
            setError(errorData.detail || 'Ogiltigt eller utgånget inbjudningskod.');
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('❌ Invitation validation error:', err);
          setError('Ett fel uppstod vid validering av inbjudningskod.');
          setLoading(false);
          return;
        }
      }

      // User is logged in, first check if they already have a therapist
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        
        // Check if user already has a therapist (e.g., from invitation registration)
        console.log('🔍 Checking if user already has a therapist...');
        const therapistResponse = await fetch(`${API_BASE}/api/invitations/my-therapist`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (therapistResponse.ok) {
          const therapistData = await therapistResponse.json();
          
          if (therapistData.has_therapist) {
            // User already connected to a therapist - show success and redirect
            console.log('✅ User already has a therapist, skipping invitation use');
            setTherapistInfo(therapistData.therapist);
            setSuccess(`🎉 Du är redan ansluten till ${therapistData.therapist.name}!`);
            
            // Redirect to patient dashboard after 2 seconds
            setTimeout(() => {
              navigate('/client', { replace: true });
            }, 2000);
            setLoading(false);
            return;
          }
        } else if (therapistResponse.status === 401) {
          // Token is invalid/expired - clear it and treat as unauthenticated user
          console.log('🔑 Token expired/invalid, clearing and showing registration options');
          localStorage.removeItem('therapy_auth_token');
          
          // Validate invitation and show registration options
          try {
            const validateResponse = await fetch(`${API_BASE}/api/invitations/validate-invitation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                invitation_code: code.toUpperCase()
              })
            });

            if (validateResponse.ok) {
              const invitationData = await validateResponse.json();
              setInvitationInfo(invitationData);
              setShowRegistrationOption(true);
              setLoading(false);
              return;
            } else {
              const errorData = await validateResponse.json();
              setError(errorData.detail || 'Ogiltigt eller utgånget inbjudningskod.');
              setLoading(false);
              return;
            }
          } catch (validationErr) {
            console.error('❌ Invitation validation error after token expiry:', validationErr);
            setError('Ett fel uppstod vid validering av inbjudningskod.');
            setLoading(false);
            return;
          }
        }

        // User doesn't have a therapist yet, try to use the invitation code
        console.log('🔄 User has no therapist, attempting to use invitation code...');
        const response = await fetch(`${API_BASE}/api/invitations/use-invitation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            invitation_code: code.toUpperCase()
          })
        });

        const data = await response.json();

        if (response.ok) {
          setTherapistInfo(data.therapist);
          setSuccess(`🎉 Grattis! Du är nu ansluten till ${data.therapist.name}!`);
          
          // Redirect to patient dashboard after 3 seconds
          setTimeout(() => {
            navigate('/client', { replace: true });
          }, 3000);
        } else if (response.status === 401) {
          // Token expired during invitation use - clear token and show registration options
          console.log('🔑 Token expired during invitation use, clearing and showing registration options');
          localStorage.removeItem('therapy_auth_token');
          
          // Validate invitation and show registration options
          try {
            const validateResponse = await fetch(`${API_BASE}/api/invitations/validate-invitation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                invitation_code: code.toUpperCase()
              })
            });

            if (validateResponse.ok) {
              const invitationData = await validateResponse.json();
              setInvitationInfo(invitationData);
              setShowRegistrationOption(true);
              setLoading(false);
              return;
            } else {
              const errorData = await validateResponse.json();
              setError(errorData.detail || 'Ogiltigt eller utgånget inbjudningskod.');
              setLoading(false);
              return;
            }
          } catch (validationErr) {
            console.error('❌ Invitation validation error after use-invitation token expiry:', validationErr);
            setError('Ett fel uppstod vid validering av inbjudningskod.');
            setLoading(false);
            return;
          }
        } else {
          setError(data.detail || 'Ogiltigt eller utgånget inbjudningskod.');
          console.error('❌ Invitation failed:', {
            status: response.status,
            statusText: response.statusText,
            data: data
          });
        }
      } catch (err) {
        setError('Ett fel uppstod. Försök igen senare.');
        console.error('❌ Invitation error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      handleInvite();
    } else {
      setError('Ingen inbjudningskod hittades.');
      setLoading(false);
    }
  }, [code, navigate]);

  const handleCreateAccount = () => {
    // Navigate to invitation registration with invitation context
    navigate('/invitation-register', { 
      state: { 
        invitationCode: code.toUpperCase(),
        invitationInfo: invitationInfo,
        returnTo: `/invite/${code}`
      }
    });
  };

  const handleExistingAccount = () => {
    // Navigate to login with invitation context
    navigate('/login', { 
      state: { 
        returnTo: `/invite/${code}`,
        message: 'Logga in för att använda din inbjudningskod och ansluta till din terapeut'
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen ai-bg flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Ansluter dig till din terapeut...</h1>
          <p className="text-gray-600">Behandlar din inbjudningskod: <span className="font-mono font-bold text-blue-600">{code}</span></p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen ai-bg flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Anslutning lyckades!</h1>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800 font-semibold">{success}</p>
            {therapistInfo && (
              <div className="mt-3 text-sm text-green-700">
                <p><strong>Terapeut:</strong> {therapistInfo.name}</p>
                <p><strong>E-post:</strong> {therapistInfo.email}</p>
              </div>
            )}
          </div>
          <p className="text-gray-600 mb-4">Du kommer att omdirigeras till din instrumentpanel...</p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen ai-bg flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Något gick fel</h1>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/connect-therapist')}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Ange kod manuellt
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Logga in igen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show registration option for new users with valid invitation
  if (showRegistrationOption && invitationInfo) {
    return (
      <div className="min-h-screen ai-bg flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Giltig inbjudan! 🎉</h1>
            <p className="text-gray-600 mb-6">
              <strong className="text-blue-600">{invitationInfo.therapist.name}</strong> har bjudit in dig till terapi.
            </p>
          </div>

          {/* Personal message if available */}
          {invitationInfo.personal_message && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Personligt meddelande:</h3>
              <p className="text-blue-800 italic">"{invitationInfo.personal_message}"</p>
            </div>
          )}

          {/* Invitation details */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Inbjudningsinformation:</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Terapeut:</strong> {invitationInfo.therapist.name}</p>
              <p><strong>Kod:</strong> <span className="font-mono bg-gray-200 px-2 py-1 rounded">{code}</span></p>
              {invitationInfo.patient_email && (
                <p><strong>För:</strong> {invitationInfo.patient_email}</p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-4">
            <button
              onClick={handleCreateAccount}
              className="w-full bg-gradient-to-r from-green-500 to-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Skapa nytt konto och anslut
            </button>
            
            <div className="text-center">
              <span className="text-gray-500 text-sm">eller</span>
            </div>
            
            <button
              onClick={handleExistingAccount}
              className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Jag har redan ett konto
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Genom att fortsätta godkänner du att ansluta till {invitationInfo.therapist.name} för terapi.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InviteHandler;
